import React, { useEffect, useRef, useState } from "react";

const VARIANTS = {
  success: {
    title: "Success",
    iconStroke: "#16a34a",
    ring: "#16a34a",
    ringTrack: "rgba(22,163,74,0.15)",
    tintFrom: "#dcfce7",
    tintTo: "#ffffff",
    iconBg: "#ffffff",
    iconBorder: "rgba(22,163,74,0.18)",
    Icon: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="8 12 11 15 16 9" />
      </svg>
    ),
  },
  warning: {
    title: "Warning",
    iconStroke: "#d97706",
    ring: "#d97706",
    ringTrack: "rgba(217,119,6,0.15)",
    tintFrom: "#fef9c3",
    tintTo: "#ffffff",
    iconBg: "#ffffff",
    iconBorder: "rgba(217,119,6,0.18)",
    Icon: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3.05h16.94a2 2 0 0 0 1.71-3.05L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
  },
  error: {
    title: "Error",
    iconStroke: "#dc2626",
    ring: "#dc2626",
    ringTrack: "rgba(220,38,38,0.15)",
    tintFrom: "#fee2e2",
    tintTo: "#ffffff",
    iconBg: "#ffffff",
    iconBorder: "rgba(220,38,38,0.18)",
    Icon: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
  },
};

const ToastUI = ({
  type = "success",
  title,
  message = "Anyone with a link can now view this file.",
  duration = 4000,
  onClose,
  open = true,
}) => {
  const variant = VARIANTS[type] || VARIANTS.success;
  const [phase, setPhase] = useState("enter"); // "enter" | "shown" | "leave"
  const [paused, setPaused] = useState(false);
  const [collapseHeight, setCollapseHeight] = useState(null);
  const elapsedRef = useRef(0);
  const startedAtRef = useRef(0);
  const rafRef = useRef(null);
  const innerRef = useRef(null);

  const ENTER_MS = 620;
  const LEAVE_MS = 380;

  // mount: enter -> shown
  useEffect(() => {
    if (!open) return;
    setPhase("enter");
    const id = setTimeout(() => setPhase("shown"), ENTER_MS);
    return () => clearTimeout(id);
  }, [open]);

  const dismiss = () => {
    if (phase === "leave") return;
    const h = innerRef.current ? innerRef.current.offsetHeight : null;
    if (h != null) setCollapseHeight(h);
    setPhase("leave");
    // Next frame: collapse height to 0 so CSS transition runs
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setCollapseHeight(0));
    });
    setTimeout(() => onClose && onClose(), LEAVE_MS);
  };

  // progress + auto close
  useEffect(() => {
    if (!open || phase === "leave" || !duration) return;

    const tick = (now) => {
      if (paused) {
        startedAtRef.current = now;
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const delta = now - startedAtRef.current;
      startedAtRef.current = now;
      elapsedRef.current = Math.min(elapsedRef.current + delta, duration);
      if (elapsedRef.current >= duration) {
        dismiss();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    startedAtRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, paused, phase]);

  if (!open) return null;

  const keyframes = `
    @keyframes toast_slideIn {
      0%   { transform: translateX(120%) scale(0.92); filter: blur(2px); }
      60%  { transform: translateX(-10px) scale(1.02); filter: blur(0); }
      80%  { transform: translateX(4px) scale(0.99); }
      100% { transform: translateX(0) scale(1); filter: blur(0); }
    }
    @keyframes toast_slideOut {
      0%   { transform: translateX(0) scale(1); }
      20%  { transform: translateX(-8px) scale(0.99); }
      100% { transform: translateX(120%) scale(0.94); filter: blur(2px); }
    }
    @keyframes toast_iconPop {
      0%   { transform: scale(0.4) rotate(-12deg); opacity: 0; }
      60%  { transform: scale(1.18) rotate(0deg);  opacity: 1; }
      100% { transform: scale(1)    rotate(0deg);  opacity: 1; }
    }
  `;

  const Icon = variant.Icon;

  const isLeaving = phase === "leave";

  // Outer wrapper: handles height collapse so stacked toasts reflow on exit
  const wrapperStyle = {
    overflow: "hidden",
    pointerEvents: isLeaving ? "none" : "auto",
    height: isLeaving ? (collapseHeight ?? "auto") : "auto",
    transition: `height 320ms cubic-bezier(0.4, 0, 0.2, 1) 60ms`,
    willChange: "height",
    borderRadius: 16,
  };

  return (
    <div style={wrapperStyle}>
      <div
        ref={innerRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        role="status"
        aria-live="polite"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 580,
          minWidth: 460,
          display: "flex",
          alignItems: "center",
          gap: 18,
          padding: "20px 22px",
          borderRadius: 16,
          background: `linear-gradient(105deg, ${variant.tintFrom} 0%, ${variant.tintTo} 65%)`,
          border: "1px solid rgba(15, 23, 42, 0.06)",
          boxShadow:
            "0 16px 32px -10px rgba(15, 23, 42, 0.22), 0 6px 12px -4px rgba(15, 23, 42, 0.10)",
          fontFamily:
            "'Inter', 'DM Sans', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          animation: isLeaving
            ? `toast_slideOut ${LEAVE_MS}ms cubic-bezier(0.4, 0, 1, 1) forwards`
            : `toast_slideIn ${ENTER_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1) both`,
          pointerEvents: isLeaving ? "none" : "auto",
          overflow: "hidden",
          willChange: "transform, filter",
          backgroundClip: "padding-box",
        }}
      >
        <style>{keyframes}</style>
        {/* Icon */}
        <div
          style={{
            flexShrink: 0,
            color: variant.iconStroke,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: isLeaving
              ? undefined
              : "toast_iconPop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) 0.18s both",
          }}
        >
          <Icon />
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: "#0f172a",
              lineHeight: 1.3,
              letterSpacing: "-0.01em",
            }}
          >
            {title || variant.title}
          </div>
          <div
            style={{
              marginTop: 3,
              fontSize: 13.5,
              color: "#64748b",
              fontWeight: 500,
              lineHeight: 1.5,
            }}
          >
            {message}
          </div>
        </div>

        {/* Close */}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          style={{
            flexShrink: 0,
            width: 32,
            height: 32,
            borderRadius: 8,
            border: "none",
            background: "transparent",
            color: "#94a3b8",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.15s ease, color 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(15,23,42,0.05)";
            e.currentTarget.style.color = "#475569";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "#94a3b8";
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export const ToastContainer = ({
  position = "top-right",
  children,
  gap = 10,
  offset = 8,
}) => {
  const positionStyle = {
    "top-right":    { top: offset, right: offset, alignItems: "flex-end" },
    "top-left":     { top: offset, left: offset,  alignItems: "flex-start" },
    "top-center":   { top: offset, left: "50%", transform: "translateX(-50%)", alignItems: "center" },
    "bottom-right": { bottom: offset, right: offset, alignItems: "flex-end" },
    "bottom-left":  { bottom: offset, left: offset,  alignItems: "flex-start" },
  }[position] || {};

  return (
    <div
      style={{
        position: "fixed",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap,
        pointerEvents: "none",
        ...positionStyle,
      }}
    >
      {children}
    </div>
  );
};

export default ToastUI;
